'use client';
import React, { useState, useMemo } from 'react';
import Link from 'next/link';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import AppImage from '@/components/ui/AppImage';
import { fireFloodlightPixel, FLOODLIGHT_ACTIVITIES } from '@/lib/floodlight';
import { useFloodlightPageview } from '@/lib/useFloodlightPageview';

const BASE = process.env.NEXT_PUBLIC_BASE_PATH || '';

interface Product {
  id: string;
  name: string;
  tagline: string;
  description: string;
  image: string;
  alt: string;
  badge?: string;
  badgeColor?: string;
  accent: string;
  textAccent: string;
  category: string;
  protein: number;
  calories: number;
  nutrients: { label: string; value: string }[];
  packSizes: string[];
  dietary: string[];
}

const products: Product[] = [
  {
    id: 'chocolate-chhatua',
    name: 'Chocolate Chhatua',
    tagline: 'Kids love it. Parents approve it.',
    description: 'Ideal breakfast option packed with multi-grains and chocolate powder.',
    image: '/assets/images/product_images/chocolate-chhatua.jpg',
    alt: 'Kasturi Chocolate Chhatua pack with chocolate powder and multi-grain ingredients',
    badge: 'Kids Favourite',
    badgeColor: '#E8A838',
    accent: '#3D1F0A',
    textAccent: '#E8A838',
    category: 'chhatua',
    protein: 11,
    calories: 310,
    nutrients: [
      { label: 'Energy', value: '310 kcal' },
      { label: 'Protein', value: '11g' },
      { label: 'Carbs', value: '75g' },
      { label: 'Fat', value: '7g' },
    ],
    packSizes: ['250g', '500g', '1kg'],
    dietary: ['multi-grain'],
  },
  {
    id: 'mix-chhatua',
    name: 'Mix Chhatua',
    tagline: 'Wholesome. Balanced. Daily.',
    description: 'Balance your nutritional and lifestyle demands with the wholesomeness of multi-grains.',
    image: '/assets/images/product_images/mix-chhatua.jpg',
    alt: 'Kasturi Mix Chhatua multi-grain product pack',
    accent: '#1A0F05',
    textAccent: '#C17B2F',
    category: 'chhatua',
    protein: 11,
    calories: 310,
    nutrients: [
      { label: 'Energy', value: '310 kcal' },
      { label: 'Protein', value: '11g' },
      { label: 'Carbs', value: '75g' },
      { label: 'Fibre', value: '1.8g' },
    ],
    packSizes: ['250g', '500g', '1kg'],
    dietary: ['multi-grain', 'high-fibre'],
  },
  {
    id: 'sugar-free-chhatua',
    name: 'Sugar-Free Chhatua',
    tagline: 'Zero added sugar. Full nutrition.',
    description: 'For health-conscious households that refuse to compromise on taste.',
    image: '/assets/images/product_images/sugar-free-chhatua.jpg',
    alt: 'Kasturi Sugar-Free Chhatua pack with zero added sugar label',
    badge: 'Sugar Free',
    badgeColor: '#4CAF50',
    accent: '#0D1A0A',
    textAccent: '#7BC67E',
    category: 'chhatua',
    protein: 13,
    calories: 293,
    nutrients: [
      { label: 'Energy', value: '293 kcal' },
      { label: 'Protein', value: '13g' },
      { label: 'Carbs', value: '73g' },
      { label: 'Added Sugar', value: '0g' },
    ],
    packSizes: ['250g', '500g'],
    dietary: ['sugar-free', 'diabetic-friendly'],
  },
  {
    id: 'chana-sattu',
    name: 'Chana Sattu',
    tagline: 'Ancient protein. Modern kitchen.',
    description: 'Smooth, lump-free batter — the perfect choice for breakfast. 20g protein per 100g.',
    image: '/assets/images/product_images/chana-sattu.jpg',
    alt: 'Kasturi Chana Sattu pack showing high protein content with roasted gram imagery',
    badge: 'High Protein',
    badgeColor: '#E8A838',
    accent: '#2A1500',
    textAccent: '#E8A838',
    category: 'sattu',
    protein: 20,
    calories: 400,
    nutrients: [
      { label: 'Energy', value: '400 kcal' },
      { label: 'Protein', value: '20g' },
      { label: 'Fibre', value: '12g' },
      { label: 'Added Sugar', value: '0g' },
    ],
    packSizes: ['500g', '1kg'],
    dietary: ['high-protein', 'sugar-free'],
  },
  {
    id: 'chakki-atta',
    name: 'Select Chakki Atta',
    tagline: 'Fortified. Fresh. Fundamental.',
    description: 'Essential micronutrients — Iron, Folic Acid, Vitamin B12 — in every roti.',
    image: '/assets/images/product_images/chakki-atta.jpg',
    alt: 'Kasturi Select Chakki Atta bag with freshly milled wheat flour',
    badge: 'Fortified',
    badgeColor: '#C17B2F',
    accent: '#1C1005',
    textAccent: '#D4A96A',
    category: 'flour',
    protein: 11,
    calories: 360,
    nutrients: [
      { label: 'Energy', value: '360 kcal' },
      { label: 'Protein', value: '11.25g' },
      { label: 'Fibre', value: '13.6g' },
      { label: 'Cholesterol', value: '0mg' },
    ],
    packSizes: ['1kg', '2kg', '5kg'],
    dietary: ['fortified', 'high-fibre'],
  },
  {
    id: 'ragi-flour',
    name: 'Ragi Flour',
    tagline: 'Calcium-rich. Bone-strong.',
    description: 'Wholesome finger millet flour packed with calcium, iron, and dietary fibre.',
    image: '/assets/images/product_images/ragi-flour.jpg',
    alt: 'Kasturi Ragi Flour 500g pack showing finger millet grain and nutritional information',
    badge: 'New',
    badgeColor: '#E8A838',
    accent: '#0F0A02',
    textAccent: '#C17B2F',
    category: 'flour',
    protein: 11,
    calories: 382,
    nutrients: [
      { label: 'Calories', value: '382 kcal' },
      { label: 'Protein', value: '11g' },
      { label: 'Carbs', value: '75g' },
      { label: 'Fibre', value: '3.5g' },
    ],
    packSizes: ['500g', '1kg'],
    dietary: ['gluten-free', 'high-fibre'],
  },
];

const categories = [
  { id: 'all', label: 'All Products' },
  { id: 'chhatua', label: 'Chhatua' },
  { id: 'sattu', label: 'Sattu' },
  { id: 'flour', label: 'Flour' },
];

const sortOptions = [
  { id: 'default', label: 'Featured' },
  { id: 'protein-high', label: 'Highest Protein' },
  { id: 'calories-low', label: 'Lowest Calories' },
  { id: 'name-az', label: 'Name A–Z' },
];

const dietaryFilters = [
  { id: 'high-protein', label: 'High Protein' },
  { id: 'sugar-free', label: 'Sugar Free' },
  { id: 'gluten-free', label: 'Gluten Free' },
  { id: 'high-fibre', label: 'High Fibre' },
  { id: 'diabetic-friendly', label: 'Diabetic Friendly' },
  { id: 'fortified', label: 'Fortified' },
];

function ProductCard({ product }: { product: Product }) {
  return (
    <Link
      href={`/products/${product.id}`}
      className="group flex flex-col rounded-xl overflow-hidden transition-all duration-300 hover:-translate-y-1"
      style={{ background: 'var(--card)', border: '1px solid var(--border)', boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
      {/* Product image */}
      <div className="relative w-full aspect-[4/5] shrink-0 overflow-hidden" style={{ background: '#FDF8F2' }}>
        <AppImage
          src={product.image}
          alt={product.alt}
          fill
          sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
          style={{ objectFit: 'contain' }}
          className="p-2 transition-transform duration-500 group-hover:scale-105"
        />
        {product.badge && (
          <span
            className="absolute top-3 left-3 text-[9px] font-bold uppercase tracking-[0.2em] px-3 py-1.5 rounded-full"
            style={{
              background: `${product.badgeColor}22`,
              color: product.badgeColor,
              border: `1px solid ${product.badgeColor}44`,
              backdropFilter: 'blur(6px)',
            }}>
            {product.badge}
          </span>
        )}
      </div>
      {/* Content */}
      <div className="p-5 flex flex-col flex-1" style={{ background: '#2A1015' }}>
        <h3
          className="font-display font-[200] leading-tight"
          style={{ fontSize: 'clamp(1.05rem, 1.6vw, 1.3rem)', color: '#FDF8F2', letterSpacing: '0.02em' }}>
          {product.name}
        </h3>
        <p className="mt-1.5 text-xs tracking-wide leading-relaxed" style={{ color: 'rgba(253,248,242,0.7)' }}>
          {product.tagline}
        </p>
        <div className="flex flex-wrap gap-1.5 mt-3">
          {product.nutrients.slice(0, 2).map((n) => (
            <span
              key={n.label}
              className="text-[9px] font-semibold uppercase tracking-wider px-2 py-1 rounded"
              style={{
                background: 'rgba(253,248,242,0.08)',
                color: 'rgba(253,248,242,0.75)',
                border: `1px solid rgba(253,248,242,0.1)`,
              }}>
              {n.value} {n.label}
            </span>
          ))}
        </div>
        <div
          className="mt-auto pt-5 flex items-center gap-2 text-xs font-bold uppercase tracking-wider transition-all duration-200 group-hover:gap-3"
          style={{ color: '#F9DC2A' }}>
          View Product
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M5 12h14M12 5l7 7-7 7" />
          </svg>
        </div>
      </div>
    </Link>
  );
}

function FilterSidebar({
  activeCategory,
  setActiveCategory,
  activeDietary,
  toggleDietary,
  proteinMin,
  setProteinMin,
  onClear,
}: {
  activeCategory: string;
  setActiveCategory: (c: string) => void;
  activeDietary: string[];
  toggleDietary: (d: string) => void;
  proteinMin: number;
  setProteinMin: (v: number) => void;
  onClear: () => void;
}) {
  const hasFilters = activeCategory !== 'all' || activeDietary.length > 0 || proteinMin > 0;

  return (
    <aside className="w-full lg:w-56 shrink-0">
      <div
        className="rounded-lg p-5 sticky top-[120px]"
        style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
        <div className="flex items-center justify-between mb-5">
          <span className="text-xs font-bold uppercase tracking-widest" style={{ color: 'var(--foreground)' }}>
            Filters
          </span>
          {hasFilters && (
            <button
              onClick={onClear}
              className="text-[10px] font-semibold uppercase tracking-wider transition-opacity hover:opacity-70"
              style={{ color: 'var(--accent-ink)' }}>
              Clear all
            </button>
          )}
        </div>

        {/* Category */}
        <div className="mb-6">
          <p className="text-[10px] font-bold uppercase tracking-widest mb-3" style={{ color: 'var(--muted-foreground)' }}>
            Category
          </p>
          <div className="flex flex-col gap-1.5">
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                className="flex items-center gap-2.5 px-3 py-2 rounded text-xs font-semibold text-left transition-all duration-150"
                style={
                  activeCategory === cat.id
                    ? { background: '#2A1015', color: '#FDF8F2' }
                    : { background: 'transparent', color: 'var(--muted-foreground)' }
                }>
                <span
                  className="w-1.5 h-1.5 rounded-full shrink-0"
                  style={{ background: activeCategory === cat.id ? '#F9DC2A' : 'var(--border)' }}
                />
                {cat.label}
              </button>
            ))}
          </div>
        </div>

        {/* Dietary */}
        <div className="mb-6">
          <p className="text-[10px] font-bold uppercase tracking-widest mb-3" style={{ color: 'var(--muted-foreground)' }}>
            Dietary
          </p>
          <div className="flex flex-col gap-1.5">
            {dietaryFilters.map((d) => (
              <button
                key={d.id}
                onClick={() => toggleDietary(d.id)}
                className="flex items-center gap-2.5 px-3 py-2 rounded text-xs font-semibold text-left transition-all duration-150"
                style={
                  activeDietary.includes(d.id)
                    ? { background: '#F9DC2A18', color: '#8A6A10', border: '1px solid #F9DC2A33' }
                    : { background: 'transparent', color: 'var(--muted-foreground)' }
                }>
                <span
                  className="w-3.5 h-3.5 rounded border flex items-center justify-center shrink-0 transition-all"
                  style={{
                    borderColor: activeDietary.includes(d.id) ? '#F9DC2A' : 'var(--border)',
                    background: activeDietary.includes(d.id) ? '#F9DC2A' : 'transparent',
                  }}>
                  {activeDietary.includes(d.id) && (
                    <svg width="8" height="8" viewBox="0 0 12 12" fill="none">
                      <path d="M2 6l3 3 5-5" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )}
                </span>
                {d.label}
              </button>
            ))}
          </div>
        </div>

        {/* Min Protein */}
        <div>
          <p className="text-[10px] font-bold uppercase tracking-widest mb-3" style={{ color: 'var(--muted-foreground)' }}>
            Min. Protein
          </p>
          <div className="flex items-center gap-3">
            <input
              type="range"
              min={0}
              max={20}
              step={1}
              value={proteinMin}
              onChange={(e) => setProteinMin(Number(e.target.value))}
              className="flex-1"
              style={{ accentColor: '#F9DC2A' }}
            />
            <span className="text-xs font-bold w-10 text-right" style={{ color: 'var(--foreground)' }}>
              {proteinMin}g+
            </span>
          </div>
          <div className="flex justify-between mt-1">
            <span className="text-[9px]" style={{ color: 'var(--muted-foreground)' }}>0g</span>
            <span className="text-[9px]" style={{ color: 'var(--muted-foreground)' }}>20g</span>
          </div>
        </div>
      </div>
    </aside>
  );
}

export default function ProductsClient() {
  useFloodlightPageview(FLOODLIGHT_ACTIVITIES.productListingPage);

  const [activeCategory, setActiveCategory] = useState('all');
  const [activeSort, setActiveSort] = useState('default');
  const [activeDietary, setActiveDietary] = useState<string[]>([]);
  const [proteinMin, setProteinMin] = useState(0);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  const toggleDietary = (id: string) => {
    setActiveDietary((prev) =>
      prev.includes(id) ? prev.filter((d) => d !== id) : [...prev, id]
    );
  };

  const clearFilters = () => {
    setActiveCategory('all');
    setActiveDietary([]);
    setProteinMin(0);
  };

  const filtered = useMemo(() => {
    let list = activeCategory === 'all' ? products : products.filter((p) => p.category === activeCategory);
    if (activeDietary.length > 0) {
      list = list.filter((p) => activeDietary.every((d) => p.dietary.includes(d)));
    }
    if (proteinMin > 0) {
      list = list.filter((p) => p.protein >= proteinMin);
    }
    if (activeSort === 'protein-high') list = [...list].sort((a, b) => b.protein - a.protein);
    else if (activeSort === 'calories-low') list = [...list].sort((a, b) => a.calories - b.calories);
    else if (activeSort === 'name-az') list = [...list].sort((a, b) => a.name.localeCompare(b.name));
    return list;
  }, [activeCategory, activeSort, activeDietary, proteinMin]);

  const hasFilters = activeCategory !== 'all' || activeDietary.length > 0 || proteinMin > 0;

  return (
    <div style={{ background: 'var(--background)', minHeight: '100vh' }}>
      <Header />

      {/* Page Hero */}
      <section
        className="relative overflow-hidden pt-32 pb-16"
        style={{ background: 'linear-gradient(160deg, #2A1015 0%, #472029 50%, #170A0D 100%)' }}>
        <div
          className="absolute inset-0 pointer-events-none opacity-[0.05]"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
            backgroundSize: '200px 200px',
          }}
        />
        <div
          className="absolute top-0 right-0 w-[600px] h-[400px] pointer-events-none"
          style={{ background: 'radial-gradient(ellipse at top right, rgba(249,220,42,0.15) 0%, transparent 65%)' }}
        />
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <div className="flex items-center gap-2 mb-6">
            <Link
              href="/"
              className="text-xs font-medium uppercase tracking-widest transition-colors"
              style={{ color: 'rgba(253,248,242,0.45)' }}
              onMouseEnter={(e) => ((e.target as HTMLElement).style.color = 'rgba(253,248,242,0.8)')}
              onMouseLeave={(e) => ((e.target as HTMLElement).style.color = 'rgba(253,248,242,0.45)')}>
              Home
            </Link>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="rgba(253,248,242,0.3)" strokeWidth="2" strokeLinecap="round">
              <path d="M9 18l6-6-6-6" />
            </svg>
            <span className="text-xs font-medium uppercase tracking-widest" style={{ color: '#F9DC2A' }}>
              Products
            </span>
          </div>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.3em] mb-3" style={{ color: '#F9DC2A' }}>
                Clean Label · No Preservatives · FSSAI Certified
              </p>
              <h1
                className="font-display font-[200] leading-none"
                style={{ fontSize: 'clamp(2.5rem, 6vw, 4.5rem)', color: '#FDF8F2', letterSpacing: '0.02em' }}>
                Our Products
              </h1>
              <p className="mt-4 text-base leading-relaxed max-w-xl" style={{ color: 'rgba(253,248,242,0.6)' }}>
                Browse, filter by dietary needs, and order instantly on Blinkit, Instamart, Amazon, or Flipkart.
              </p>
            </div>
            <div className="hidden lg:flex items-end -space-x-6 shrink-0 self-end -mb-4">
              {['chana-sattu','mix-chhatua','chocolate-chhatua','sugar-free-chhatua','ragi-flour'].map((img, i) => (
                <img key={img} src={`${BASE}/assets/images/product_images/${img}.jpg`} alt="" loading="lazy"
                  className="h-28 xl:h-36 w-auto rounded-lg shadow-2xl object-cover"
                  style={{ transform: `rotate(${(i - 2) * 4}deg) translateY(${Math.abs(i - 2) * 6}px)`, border: '2px solid rgba(253,248,242,0.15)' }} />
              ))}
            </div>
            <div className="flex gap-8 shrink-0 md:hidden">
              {[
                { value: '6', label: 'Products' },
                { value: '0', label: 'Preservatives' },
                { value: '100%', label: 'Clean Label' },
              ].map((stat) => (
                <div key={stat.label} className="text-center">
                  <div
                    className="font-display font-[200]"
                    style={{ fontSize: 'clamp(1.5rem, 3vw, 2.2rem)', color: '#F9DC2A', letterSpacing: '0.02em' }}>
                    {stat.value}
                  </div>
                  <div className="text-[10px] uppercase tracking-widest mt-0.5" style={{ color: 'rgba(253,248,242,0.45)' }}>
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Sort Bar */}
      <section
        className="sticky top-[60px] z-30 border-b"
        style={{ background: 'rgba(253,248,242,0.97)', backdropFilter: 'blur(12px)', borderColor: 'var(--border)' }}>
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-3 flex items-center justify-between gap-3">
          {/* Mobile filter toggle */}
          <button
            className="lg:hidden flex items-center gap-2 px-3 py-1.5 rounded text-xs font-bold uppercase tracking-wider"
            style={{
              background: hasFilters ? '#2A1015' : 'transparent',
              color: hasFilters ? '#FDF8F2' : 'var(--foreground)',
              border: '1px solid var(--border)',
            }}
            onClick={() => setMobileFiltersOpen((v) => !v)}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <line x1="4" y1="6" x2="20" y2="6" />
              <line x1="8" y1="12" x2="20" y2="12" />
              <line x1="12" y1="18" x2="20" y2="18" />
            </svg>
            Filters {hasFilters && `(${(activeCategory !== 'all' ? 1 : 0) + activeDietary.length + (proteinMin > 0 ? 1 : 0)})`}
          </button>

          <div className="hidden lg:flex items-center gap-1.5 flex-wrap">
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                className="px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider transition-all duration-200"
                style={
                  activeCategory === cat.id
                    ? { background: '#2A1015', color: '#FDF8F2' }
                    : { background: 'transparent', color: 'var(--muted-foreground)', border: '1px solid var(--border)' }
                }>
                {cat.label}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-2 ml-auto">
            <span className="text-[10px] uppercase tracking-widest hidden sm:block" style={{ color: 'var(--muted-foreground)' }}>Sort:</span>
            <div className="flex items-center gap-1">
              {sortOptions.map((opt) => (
                <button
                  key={opt.id}
                  onClick={() => setActiveSort(opt.id)}
                  className="px-3 py-1 rounded text-[10px] font-semibold uppercase tracking-wider transition-all duration-200"
                  style={
                    activeSort === opt.id
                      ? { background: '#F9DC2A22', color: '#8A6A10', border: '1px solid #F9DC2A44' }
                      : { background: 'transparent', color: 'var(--muted-foreground)' }
                  }>
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Mobile filter panel */}
        {mobileFiltersOpen && (
          <div
            className="lg:hidden border-t px-6 py-4"
            style={{ borderColor: 'var(--border)', background: 'var(--card)' }}>
            <div className="flex flex-wrap gap-2 mb-4">
              <p className="w-full text-[10px] font-bold uppercase tracking-widest mb-1" style={{ color: 'var(--muted-foreground)' }}>Category</p>
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => setActiveCategory(cat.id)}
                  className="px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider transition-all"
                  style={
                    activeCategory === cat.id
                      ? { background: '#2A1015', color: '#FDF8F2' }
                      : { background: 'transparent', color: 'var(--muted-foreground)', border: '1px solid var(--border)' }
                  }>
                  {cat.label}
                </button>
              ))}
            </div>
            <div className="flex flex-wrap gap-2 mb-4">
              <p className="w-full text-[10px] font-bold uppercase tracking-widest mb-1" style={{ color: 'var(--muted-foreground)' }}>Dietary</p>
              {dietaryFilters.map((d) => (
                <button
                  key={d.id}
                  onClick={() => toggleDietary(d.id)}
                  className="px-3 py-1.5 rounded-full text-xs font-semibold transition-all"
                  style={
                    activeDietary.includes(d.id)
                      ? { background: '#F9DC2A', color: '#1A1614' }
                      : { background: 'transparent', color: 'var(--muted-foreground)', border: '1px solid var(--border)' }
                  }>
                  {d.label}
                </button>
              ))}
            </div>
            {hasFilters && (
              <button onClick={clearFilters} className="text-xs font-bold uppercase tracking-wider" style={{ color: 'var(--accent-ink)' }}>
                Clear all filters
              </button>
            )}
          </div>
        )}
      </section>

      {/* Main content: sidebar + grid */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-10 pb-28">
        <div className="flex gap-8 items-start">
          {/* Desktop sidebar */}
          <div className="hidden lg:block">
            <FilterSidebar
              activeCategory={activeCategory}
              setActiveCategory={setActiveCategory}
              activeDietary={activeDietary}
              toggleDietary={toggleDietary}
              proteinMin={proteinMin}
              setProteinMin={setProteinMin}
              onClear={clearFilters}
            />
          </div>

          {/* Products grid */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between mb-6">
              <p className="text-xs uppercase tracking-widest" style={{ color: 'var(--muted-foreground)' }}>
                Showing <strong style={{ color: 'var(--foreground)' }}>{filtered.length}</strong> product{filtered.length !== 1 ? 's' : ''}
                {activeCategory !== 'all' && ` in ${categories.find((c) => c.id === activeCategory)?.label}`}
              </p>
              {hasFilters && (
                <button
                  onClick={clearFilters}
                  className="text-[10px] font-bold uppercase tracking-wider lg:hidden"
                  style={{ color: 'var(--accent-ink)' }}>
                  Clear filters
                </button>
              )}
            </div>

            {filtered.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
                {filtered.map((product) => (
                  <ProductCard key={product.id} product={product} />
                ))}
              </div>
            ) : (
              <div className="text-center py-24 rounded-lg" style={{ border: '1px dashed var(--border)' }}>
                <div className="text-4xl mb-4">🔍</div>
                <p className="text-lg font-display font-[200]" style={{ color: 'var(--foreground)' }}>No products match your filters</p>
                <p className="mt-2 text-sm mb-6" style={{ color: 'var(--muted-foreground)' }}>Try adjusting your category, dietary, or protein filters.</p>
                <button
                  onClick={clearFilters}
                  className="px-6 py-2.5 rounded-lg text-sm font-bold uppercase tracking-wider"
                  style={{ background: '#F9DC2A', color: '#1A1614' }}>
                  Clear all filters
                </button>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section
        className="py-20 text-center"
        style={{ background: 'linear-gradient(180deg, transparent 0%, rgba(249,220,42,0.06) 100%)' }}>
        <div className="max-w-2xl mx-auto px-6">
          <p className="text-xs font-bold uppercase tracking-[0.3em] mb-3" style={{ color: '#D32328' }}>
            Available on Quick Commerce
          </p>
          <h2
            className="font-display font-[200] leading-tight mb-4"
            style={{ fontSize: 'clamp(1.8rem, 4vw, 2.8rem)', color: 'var(--foreground)', letterSpacing: '0.02em' }}>
            Ready to eat clean?
          </h2>
          <p className="text-sm leading-relaxed mb-8" style={{ color: 'var(--muted-foreground)' }}>
            Pick any product above and tap a platform icon to order instantly — Blinkit for fast delivery, or Amazon for shipped orders.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <a
              href="https://blinkit.com/"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => fireFloodlightPixel(FLOODLIGHT_ACTIVITIES.plpShopOnBlinkitButton)}
              className="inline-flex items-center gap-2.5 px-7 py-3 rounded-lg text-sm font-bold uppercase tracking-wider transition-all duration-300 hover:opacity-90"
              style={{ background: '#F9DC2A', color: '#1A1614' }}>
              <img src={`${BASE}/assets/images/blinkit.png`} alt="" className="h-5 w-auto rounded-sm" />
              Shop on Blinkit
            </a>
            <a
              href="https://www.amazon.in/s?srs=93889452031"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => fireFloodlightPixel(FLOODLIGHT_ACTIVITIES.plpShopOnAmazonButton)}
              className="inline-flex items-center gap-2.5 px-7 py-3 rounded-lg text-sm font-bold uppercase tracking-wider transition-all duration-300 hover:opacity-80"
              style={{ background: 'transparent', color: 'var(--foreground)', border: '1.5px solid var(--border)' }}>
              <img src={`${BASE}/assets/images/Amazon_icon.png`} alt="" className="h-5 w-auto" />
              Shop on Amazon
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
